import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Avatar,
  Chip,
  Button,
  Grid,
  Divider,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
} from '@mui/material';
import {
  Group,
  Add,
  AdminPanelSettings,
  Email,
  Person,
  Refresh,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { adminApi } from '../../services/adminApi';

/**
 * Admin Team Management Component
 * Fetches and manages real team members from the backend
 */
const AdminTeamManagement = () => {
  const { token } = useAuth();
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [newMember, setNewMember] = useState({ email: '', password: '', name: '', role: 'user' });
  const [addLoading, setAddLoading] = useState(false);

  const fetchTeam = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await adminApi.listUsers(token);
      setTeamMembers(Array.isArray(res?.users) ? res.users : (Array.isArray(res) ? res : []));
    } catch (err) {
      setError(err?.message || 'Failed to load team members');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchTeam(); }, [fetchTeam]);

  const handleAddMember = async () => {
    if (!newMember.email || !newMember.password || !newMember.name) return;
    setAddLoading(true);
    try {
      await adminApi.createUser(newMember, token);
      setAddOpen(false);
      setNewMember({ email: '', password: '', name: '', role: 'user' });
      await fetchTeam();
    } catch (err) {
      setError(err?.message || 'Failed to add member');
    } finally {
      setAddLoading(false);
    }
  };

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Header */}
      <Card
        sx={{
          mb: 3,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: '#fff',
        }}
      >
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar
                sx={{
                  width: 56,
                  height: 56,
                  bgcolor: 'rgba(255,255,255,0.2)',
                  backdropFilter: 'blur(10px)',
                }}
              >
                <Group sx={{ fontSize: 32 }} />
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight={800}>
                  Team Management
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {teamMembers.length} member{teamMembers.length !== 1 ? 's' : ''} registered
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                startIcon={<Refresh />}
                onClick={fetchTeam}
                disabled={loading}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                }}
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setAddOpen(true)}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.25)',
                  backdropFilter: 'blur(10px)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.35)' },
                }}
              >
                Add Member
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : teamMembers.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Group sx={{ fontSize: 64, color: 'text.secondary', opacity: 0.5, mb: 2 }} />
          <Typography variant="h6" color="text.secondary">No team members yet</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Click "Add Member" to invite your first team member
          </Typography>
        </Box>
      ) : (
      /* Team Members */
      <Grid container spacing={3}>
        {teamMembers.map((member) => (
          <Grid item xs={12} sm={6} md={4} key={member.id || member.email}>
            <Card
              sx={{
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
                },
              }}
            >
              <CardContent>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar
                      sx={{
                        width: 48,
                        height: 48,
                        bgcolor: 'primary.main',
                      }}
                    >
                      {member.name ? member.name.charAt(0).toUpperCase() : <Person />}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" fontWeight={700}>
                        {member.name || member.email}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <Email sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                        {member.email}
                      </Typography>
                    </Box>
                  </Stack>

                  <Divider />

                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                    <Chip
                      icon={<AdminPanelSettings />}
                      label={member.role || 'user'}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                    <Chip
                      label={member.status === 'active' || member.is_active ? 'Active' : 'Inactive'}
                      size="small"
                      color={member.status === 'active' || member.is_active ? 'success' : 'default'}
                    />
                  </Stack>

                  <Stack direction="row" spacing={1}>
                    <Button size="small" variant="outlined" fullWidth>
                      Edit
                    </Button>
                    <Button size="small" variant="outlined" color="error" fullWidth>
                      Remove
                    </Button>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}

        {/* Add New Member Card */}
        <Grid item xs={12} sm={6} md={4}>
          <Card
            onClick={() => setAddOpen(true)}
            sx={{
              borderRadius: 2,
              border: '2px dashed',
              borderColor: 'primary.main',
              bgcolor: 'rgba(102, 126, 234, 0.05)',
              minHeight: 250,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              '&:hover': {
                bgcolor: 'rgba(102, 126, 234, 0.1)',
                transform: 'scale(1.02)',
              },
            }}
          >
            <CardContent>
              <Stack spacing={2} alignItems="center" justifyContent="center">
                <Avatar
                  sx={{
                    width: 64,
                    height: 64,
                    bgcolor: 'primary.main',
                  }}
                >
                  <Add sx={{ fontSize: 32 }} />
                </Avatar>
                <Typography variant="h6" fontWeight={700} color="primary">
                  Add New Member
                </Typography>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  Invite a team member to collaborate
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      )}

      {/* Add Member Dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Add New Team Member</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Full Name"
              fullWidth
              value={newMember.name}
              onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
              required
            />
            <TextField
              label="Email Address"
              type="email"
              fullWidth
              value={newMember.email}
              onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
              required
            />
            <TextField
              label="Password"
              type="password"
              fullWidth
              value={newMember.password}
              onChange={(e) => setNewMember({ ...newMember, password: e.target.value })}
              helperText="Min 8 characters with uppercase, lowercase, number, and special character"
              required
            />
            <TextField
              label="Role"
              select
              fullWidth
              value={newMember.role}
              onChange={(e) => setNewMember({ ...newMember, role: e.target.value })}
            >
              <MenuItem value="user">User</MenuItem>
              <MenuItem value="manager">Manager</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddMember}
            disabled={addLoading || !newMember.email || !newMember.password || !newMember.name}
          >
            {addLoading ? <CircularProgress size={20} /> : 'Add Member'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminTeamManagement;
